/**
 * TimingPieChart Component
 *
 * Displays a pie/doughnut chart showing the percentage distribution of time
 * across different profiled operations.
 */

import React, {useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from 'chart.js';
import {Doughnut} from 'react-chartjs-2';
import {ProfileSession} from '../../@types/profiler';

// Register Chart.js components
ChartJS.register(
    ArcElement,
    Tooltip,
    Legend
);

interface TimingPieChartProps {
    session: ProfileSession;
    height?: number;
}

/**
 * Color palette for pie chart segments
 */
const COLORS = [
    'rgba(33, 150, 243, 0.8)',   // Blue
    'rgba(76, 175, 80, 0.8)',    // Green
    'rgba(255, 152, 0, 0.8)',    // Orange
    'rgba(156, 39, 176, 0.8)',   // Purple
    'rgba(244, 67, 54, 0.8)',    // Red
    'rgba(0, 188, 212, 0.8)',    // Cyan
    'rgba(255, 193, 7, 0.8)',    // Amber
    'rgba(96, 125, 139, 0.8)',   // Blue Grey
    'rgba(233, 30, 99, 0.8)',    // Pink
    'rgba(139, 195, 74, 0.8)',   // Light Green
];

const TimingPieChart: React.FC<TimingPieChartProps> = ({ session, height = 200 }) => {
    const chartData = useMemo(() => {
        const labels = session.entries.map(e => e.label);
        const data = session.entries.map(e => e.duration);
        const backgroundColors = session.entries.map((_, i) => COLORS[i % COLORS.length]);

        return {
            labels,
            datasets: [{
                data,
                backgroundColor: backgroundColors,
                borderColor: backgroundColors.map(c => c.replace('0.8', '1')),
                borderWidth: 1,
            }]
        };
    }, [session]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'right' as const,
                labels: {
                    boxWidth: 12,
                    font: {
                        size: 10
                    },
                    generateLabels: (chart: ChartJS): { text: string; fillStyle: string; strokeStyle: string; lineWidth: number; hidden: boolean; index: number }[] => {
                        const data = chart.data;
                        if (data.labels && data.datasets.length) {
                            return data.labels.map((label, i) => {
                                const entry = session.entries[i];
                                const dataset = data.datasets[0];
                                const bgColors = dataset.backgroundColor as string[];
                                const borderColors = dataset.borderColor as string[];
                                return {
                                    text: `${label} (${entry?.percentage.toFixed(1)}%)`,
                                    fillStyle: bgColors[i],
                                    strokeStyle: borderColors[i],
                                    lineWidth: 1,
                                    hidden: false,
                                    index: i
                                };
                            });
                        }
                        return [];
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: (context: { dataIndex: number; parsed: number }): string => {
                        const entry = session.entries[context.dataIndex];
                        return `${entry.duration.toFixed(1)}ms (${entry.percentage.toFixed(1)}%)`;
                    }
                }
            }
        },
        animation: {
            duration: 300
        },
        cutout: '50%'
    }), [session]);

    if (!session.entries.length) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">No timing data available</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ height, p: 1 }}>
            <Doughnut data={chartData} options={options} />
        </Box>
    );
};

export default React.memo(TimingPieChart);
